import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import { 
  Palette, Info, Type, MousePointer2, 
  Layers, CheckCircle2, AlertTriangle, XCircle,
  Layout, Code2, Sparkles
} from 'lucide-react';

const ThemeDemo = () => {
  const { themeColors, currentTheme, currentFont, currentFontSize, themeMode } = useTheme();

  const colorSwatches = [
    { name: 'Primary', color: themeColors.primary, var: '--accent-primary' },
    { name: 'Secondary', color: themeColors.secondary, var: '--secondary' },
    { name: 'Background', color: themeColors.background, var: '--bg-main' },
    { name: 'Surface / Card', color: themeColors.formBg, var: '--bg-card' },
    { name: 'Text', color: themeColors.text, var: '--text-primary' },
    { name: 'Muted Text', color: themeColors.textLight, var: '--text-secondary' },
    { name: 'Success', color: themeColors.success, var: '--success' },
    { name: 'Warning', color: themeColors.warning, var: '--warning' },
    { name: 'Error', color: themeColors.error, var: '--error' },
  ];

  return (
    <div style={{ 
      maxWidth: '1200px', 
      margin: '0 auto', 
      padding: '40px 20px',
      fontFamily: 'var(--app-font-family)',
      color: 'var(--text-primary)'
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: '40px',
        borderBottom: '2px solid rgba(var(--app-primary-color-rgb), 0.1)',
        paddingBottom: '24px'
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <div style={{ 
              width: '40px', height: '40px', borderRadius: '10px', 
              background: 'var(--accent-primary)', color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Palette size={20} />
            </div>
            <h1 style={{ margin: 0, fontSize: '1.7em', fontWeight: 900, letterSpacing: '-0.5px' }}>
              NT Monitor Theme Showcase
            </h1>
          </div>
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '1em', fontWeight: 500 }}>
            Live design system configuration for the <span style={{ color: 'var(--accent-primary)', fontWeight: 800 }}>{currentTheme.toUpperCase()}</span> theme in <span style={{ color: 'var(--accent-primary)', fontWeight: 800 }}>{themeMode.toUpperCase()} MODE</span>.
          </p>
        </div>
        
        <div style={{ 
          background: 'rgba(var(--app-primary-color-rgb), 0.1)', 
          padding: '8px 16px', 
          borderRadius: '100px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          border: '1px solid rgba(var(--app-primary-color-rgb), 0.2)'
        }}>
          <Sparkles size={14} color="var(--accent-primary)" />
          <span style={{ fontSize: '0.85em', fontWeight: 700, color: 'var(--accent-primary)' }}>Dynamic Theme System v2.0</span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '24px' }}>
        
        {/* Active Configuration */}
        <div className="dash-glass-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <Info size={18} color="var(--accent-primary)" />
            <h3 style={{ margin: 0, fontWeight: 800, fontSize: '1.15em' }}>Configuration Details</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[
              { label: 'Active Theme', value: currentTheme, bold: true },
              { label: 'Typography', value: currentFont },
              { label: 'Base Size', value: currentFontSize },
              { label: 'Color Mode', value: themeMode }
            ].map((item, i) => (
              <div key={i} style={{ 
                display: 'flex', justifyContent: 'space-between', padding: '12px 16px', 
                background: 'var(--bg-main)', borderRadius: '12px',
                border: '1px solid var(--border-light)'
              }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.9em', fontWeight: 600 }}>{item.label}</span>
                <span style={{ fontWeight: 800, fontSize: '0.9em', textTransform: 'uppercase', color: item.bold ? 'var(--accent-primary)' : 'inherit' }}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Color Palette */}
        <div className="dash-glass-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <Layers size={18} color="var(--accent-primary)" />
            <h3 style={{ margin: 0, fontWeight: 800, fontSize: '1.15em' }}>Core Palette</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {colorSwatches.map((swatch) => (
              <div key={swatch.name} style={{ 
                display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', 
                background: 'var(--bg-main)', borderRadius: '10px',
                border: '1px solid var(--border-light)'
              }}>
                <div style={{ 
                  width: '24px', height: '24px', borderRadius: '6px', 
                  background: swatch.color, border: '1px solid rgba(0,0,0,0.1)' 
                }} />
                <div style={{ overflow: 'hidden' }}>
                  <div style={{ fontSize: '0.8em', fontWeight: 800, color: 'var(--text-primary)' }}>{swatch.name}</div>
                  <div style={{ fontSize: '0.65em', color: 'var(--text-secondary)', textTransform: 'uppercase', fontFamily: 'monospace' }}>{swatch.color}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Typography */}
        <div className="dash-glass-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <Type size={18} color="var(--accent-primary)" />
            <h3 style={{ margin: 0, fontWeight: 800, fontSize: '1.15em' }}>Typography</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ fontSize: '1.6em', fontWeight: 900, lineHeight: 1.2, color: 'var(--text-primary)' }}>The quick brown fox jumps over the lazy dog.</div>
            <div style={{ fontSize: '1em', lineHeight: 1.6, color: 'var(--text-secondary)' }}>
              Using <span style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{currentFont}</span> at <span style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{currentFontSize}</span>. 
              The design system ensures maximum readability across all pages and themes.
            </div>
          </div>
        </div>

        {/* Interactive UI Components */}
        <div className="dash-glass-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <MousePointer2 size={18} color="var(--accent-primary)" />
            <h3 style={{ margin: 0, fontWeight: 800, fontSize: '1.15em' }}>UI Components</h3>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
            <button className="btn-primary" style={{ padding: '8px 20px', borderRadius: '10px' }}>Primary Button</button>
            <button className="btn-secondary" style={{ padding: '8px 20px', borderRadius: '10px' }}>Secondary Button</button>
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {['Active', 'Idle', 'Down', 'Live'].map(status => (
              <span key={status} style={{ 
                padding: '4px 10px', borderRadius: '6px', fontWeight: 800, fontSize: '0.7em',
                background: 'rgba(var(--app-primary-color-rgb), 0.12)', color: 'var(--accent-primary)',
                border: '1px solid rgba(var(--app-primary-color-rgb), 0.2)'
              }}>
                {status.toUpperCase()}
              </span>
            ))}
          </div>
        </div>

        {/* System States */}
        <div className="dash-glass-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <Layout size={18} color="var(--accent-primary)" />
            <h3 style={{ margin: 0, fontWeight: 800, fontSize: '1.15em' }}>System Indicators</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              { icon: <CheckCircle2 size={16} />, text: 'System Online', color: 'var(--success)' },
              { icon: <AlertTriangle size={16} />, text: 'Attention Required', color: 'var(--warning)' },
              { icon: <XCircle size={16} />, text: 'Critical Error', color: 'var(--error)' }
            ].map((state, i) => (
              <div key={i} style={{ 
                display: 'flex', alignItems: 'center', gap: '10px', 
                padding: '10px 14px', borderRadius: '10px', 
                background: 'var(--bg-main)', border: '1px solid var(--border-light)',
                color: state.color, fontWeight: 700, fontSize: '0.9em'
              }}>
                {state.icon}
                {state.text}
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Code Hook Reference */}
      <div className="dash-glass-card" style={{ marginTop: '24px', padding: '24px', background: 'var(--bg-card)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <Code2 size={18} color="var(--accent-primary)" />
          <h3 style={{ margin: 0, fontWeight: 800, fontSize: '1.15em', color: 'var(--text-primary)' }}>Implementation Usage</h3>
        </div>
        <code style={{ fontSize: '0.9em', background: 'var(--bg-main)', color: 'var(--accent-primary)', padding: '12px', borderRadius: '10px', display: 'block', border: '1px solid var(--border-light)' }}>
          const {'{ themeColors, currentTheme, themeMode }'} = useTheme();
        </code>
      </div>
    </div>
  );
};

export default ThemeDemo;
